import { EventManager } from '@fnv-core/events';

declare global {
  interface Window {
    _plugins: { [key: string]: Function };
  }
}

type PluginCallback = (initFunc: (Function) => void) => void;

export const PLUGIN_LOADED_EVENT = 'plugin-loaded';

/**
 * Manager for plugins to register plugins callbacks.
 */
export class PluginManager {

  private static _pluginListeners: { [name: string]: PluginCallback[] } = {};

  /**
   * Registers a plugin.
   * 
   * @param name      The name of the plugin.
   * @param initFunc  The plugin's initialization function.
   */
  static registerPlugin(name: string, initFunc: Function) {
    this._ensurePluginObject();

    window._plugins[name] = initFunc;

    // Invoke callbacks
    if (this._pluginListeners[name]) {
      this._pluginListeners[name].forEach(cb => cb(window._plugins[name]()));
    }

    EventManager.publish(PLUGIN_LOADED_EVENT, { name });
  }

  /**
   * Re-initializes a plugin. Usefull for re-initialising after an event, for example data loading.
   * 
   * @param name The name of the plugin to re-initialise.
   */
  static reInitialisePlugin(name: string) {
    this.onPluginLoad(name, () => {
      window._plugins[name]();
    });
  }

  static onPluginLoad(name: string, callback: PluginCallback) {
    this._ensurePluginObject();

    if (window._plugins[name]) {
      // Already registered, callback immediately
      callback(window._plugins[name]());
    } else {
      if (!this._pluginListeners[name]) {
        this._pluginListeners[name] = [callback];
      } else {
        this._pluginListeners[name].push(callback);
      }
    }
  }

  private static _ensurePluginObject() {
    window._plugins = window._plugins || {};
  }
}
